import { useCallback, useEffect, useState } from "react";
import { Award, GraduationCap, Plus, ShieldCheck, TriangleAlert } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type {
  AcademyCourse,
  AcademyEnrollmentRow,
  AcademyInstaller,
} from "@/lib/domain";

const LEVELS = ["silver", "gold", "platinum"] as const;

const TEXT = {
  it: {
    kicker: "VERSUS Academy",
    title: "Credenziali applicatori",
    intro:
      "Il livello si calcola dalle qualifiche in corso di validità: non si assegna a mano, e scade da solo. È il livello che finisce sul certificato.",
    installers: "Applicatori",
    courses: "Corsi",
    level: "Livello",
    validTo: "Valido fino al",
    patentino: "Patentino",
    none: "Nessuna qualifica",
    expired: "SCADUTO",
    valid: "valido",
    expiring: "in scadenza",
    credentials: "Qualifiche",
    record: "Registra una qualifica",
    course: "Corso",
    date: "Data superamento",
    score: "Punteggio",
    trainer: "Formatore",
    save: "Registra",
    history: "Storico",
    newCourse: "Nuovo corso",
    code: "Codice",
    months: "Validità (mesi)",
    required: "Richiesto per",
    close: "Chiudi",
    delete: "Elimina",
    myCredentials: "Le tue qualifiche",
    noInstaller: "Questo account non è collegato a un applicatore.",
  },
  en: {
    kicker: "VERSUS Academy",
    title: "Applicator credentials",
    intro:
      "The level is computed from credentials that are in date: it is never assigned by hand, and it lapses on its own. This is the level printed on the certificate.",
    installers: "Applicators",
    courses: "Courses",
    level: "Level",
    validTo: "Valid until",
    patentino: "Credential",
    none: "No qualification",
    expired: "EXPIRED",
    valid: "valid",
    expiring: "expiring",
    credentials: "Credentials",
    record: "Record a qualification",
    course: "Course",
    date: "Passed on",
    score: "Score",
    trainer: "Trainer",
    save: "Record",
    history: "History",
    newCourse: "New course",
    code: "Code",
    months: "Validity (months)",
    required: "Required for",
    close: "Close",
    delete: "Delete",
    myCredentials: "Your credentials",
    noInstaller: "This account is not linked to an applicator.",
  },
  es: {
    kicker: "VERSUS Academy",
    title: "Credenciales de aplicadores",
    intro:
      "El nivel se calcula a partir de las cualificaciones en vigor: no se asigna a mano y caduca por sí solo. Es el nivel que figura en el certificado.",
    installers: "Aplicadores",
    courses: "Cursos",
    level: "Nivel",
    validTo: "Válido hasta",
    patentino: "Credencial",
    none: "Sin cualificación",
    expired: "CADUCADO",
    valid: "válido",
    expiring: "por caducar",
    credentials: "Cualificaciones",
    record: "Registrar una cualificación",
    course: "Curso",
    date: "Superado el",
    score: "Puntuación",
    trainer: "Formador",
    save: "Registrar",
    history: "Historial",
    newCourse: "Nuevo curso",
    code: "Código",
    months: "Validez (meses)",
    required: "Requerido para",
    close: "Cerrar",
    delete: "Eliminar",
    myCredentials: "Tus cualificaciones",
    noInstaller: "Esta cuenta no está vinculada a un aplicador.",
  },
} as const;

const LEVEL_STYLE: Record<string, string> = {
  platinum: "border-white/40 bg-white/10 text-paper",
  gold: "border-gold/50 bg-gold/10 text-goldbright",
  silver: "border-ice/40 bg-white/5 text-ice",
  none: "border-white/15 text-ice/50",
};

const LEVEL_LABEL: Record<string, string> = {
  platinum: "VERSUS Academy Platinum",
  gold: "VERSUS Academy Gold",
  silver: "VERSUS Academy Silver",
  none: "—",
};

const d = (v?: string | null) => (v ? v.slice(0, 10) : "—");

export default function AcademyPage() {
  const { locale } = useLocale();
  const { user } = useAuth();
  const tx = TEXT[locale];
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState<"installers" | "courses">("installers");

  if (!isAdmin) return <MyCredentials tx={tx} locale={locale} />;

  return (
    <AppShell>
      <div className="brand-kicker">{tx.kicker}</div>
      <h1 className="font-serif text-2xl text-paper sm:text-3xl">{tx.title}</h1>
      <p className="mt-2 max-w-3xl text-[0.78rem] leading-relaxed text-ice/65">
        {tx.intro}
      </p>

      <div className="mt-5 flex gap-1 rounded-full border border-white/15 p-0.5">
        {(["installers", "courses"] as const).map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            className={cn(
              "rounded-full px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] transition",
              tab === tb ? "bg-white/15 text-paper" : "text-ice/60",
            )}
          >
            {tx[tb]}
          </button>
        ))}
      </div>

      {tab === "installers" ? (
        <Installers tx={tx} locale={locale} />
      ) : (
        <Courses tx={tx} locale={locale} />
      )}
    </AppShell>
  );
}

function LevelPill({
  level,
  validUntil,
  expiringSoon,
  tx,
}: {
  level: string;
  validUntil?: string | null;
  expiringSoon?: number;
  tx: Record<keyof (typeof TEXT)["it"], string>;
}) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.56rem] font-semibold uppercase tracking-[0.14em]",
          LEVEL_STYLE[level] ?? LEVEL_STYLE.none,
        )}
      >
        <Award className="h-3 w-3" />
        {level === "none" ? tx.none : level}
      </span>
      {level !== "none" && validUntil && (
        <span className="text-[0.62rem] text-ice/55">
          {tx.validTo} {d(validUntil)}
        </span>
      )}
      {expiringSoon ? (
        <span className="inline-flex items-center gap-1 text-[0.6rem] text-goldbright">
          <TriangleAlert className="h-3 w-3" />
          {expiringSoon} {tx.expiring}
        </span>
      ) : null}
    </span>
  );
}

function CredentialGrid({
  credentials,
  tx,
}: {
  credentials: AcademyInstaller["credentials"];
  tx: Record<keyof (typeof TEXT)["it"], string>;
}) {
  return (
    <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
      {credentials.map((c) => (
        <div
          key={c.courseId}
          className={cn(
            "flex items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-[0.68rem]",
            c.valid
              ? "border-white/10 bg-white/5 text-ice/80"
              : "border-rose/30 bg-rose/5 text-ice/45",
          )}
        >
          <span className="min-w-0 truncate">{c.title}</span>
          <span className="shrink-0 text-[0.6rem] uppercase tracking-[0.1em]">
            {c.valid ? d(c.expiresAt) : tx.expired}
          </span>
        </div>
      ))}
    </div>
  );
}

function Installers({
  tx,
  locale,
}: {
  tx: Record<keyof (typeof TEXT)["it"], string>;
  locale: "it" | "en" | "es";
}) {
  const [rows, setRows] = useState<AcademyInstaller[] | null>(null);
  const [courses, setCourses] = useState<AcademyCourse[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [history, setHistory] = useState<AcademyEnrollmentRow[]>([]);
  const [form, setForm] = useState({ courseId: "", completedAt: "", score: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const [inst, cs] = await Promise.all([
        api.academyInstallers(),
        api.academyCourses(),
      ]);
      setRows(inst);
      setCourses(cs);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function open(id: string) {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    setForm({ courseId: courses[0]?.id ?? "", completedAt: "", score: "" });
    try {
      const detail = await api.academyInstaller(id);
      setHistory(detail.history ?? []);
    } catch {
      setHistory([]);
    }
  }

  return (
    <div className="mt-4 grid gap-3">
      {rows === null && <p className="text-sm text-ice/60">…</p>}
      {rows?.map((i) => (
        <article key={i.id} className="glass-panel-strong p-4">
          <div className="flex flex-wrap items-start gap-3">
            <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-goldbright" />
            <div className="min-w-0 flex-1">
              <div className="font-serif text-[0.98rem] text-paper">
                {i.companyName}
              </div>
              <div className="text-[0.7rem] text-ice/60">
                {i.userName}
                {i.email ? ` · ${i.email}` : ""}
                {i.vatNumber ? ` · ${i.vatNumber}` : ""}
              </div>
              <div className="mt-2">
                <LevelPill
                  level={i.academyLevel}
                  validUntil={i.levelValidUntil}
                  expiringSoon={i.expiringSoon}
                  tx={tx}
                />
              </div>
              {i.patentinoNumber && (
                <div className="mt-1.5 flex items-center gap-1.5 text-[0.68rem] text-ice/70">
                  <ShieldCheck className="h-3.5 w-3.5 text-goldbright" />
                  <span className="num">{i.patentinoNumber}</span>
                  <span className="text-ice/45">
                    {d(i.patentinoIssuedAt)} → {d(i.patentinoExpiresAt)}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => open(i.id)}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-ice/75"
            >
              <Plus className="h-3 w-3" />
              {tx.record}
            </button>
          </div>

          <CredentialGrid credentials={i.credentials} tx={tx} />

          {openId === i.id && (
            <div className="mt-4 border-t border-white/10 pt-4">
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!form.courseId || !form.completedAt) return;
                  setBusy(true);
                  try {
                    await api.recordEnrollment(i.id, {
                      courseId: form.courseId,
                      status: "passed",
                      completedAt: form.completedAt,
                      score: form.score ? Number(form.score) : undefined,
                      trainerName: "VERSUS Academy",
                    });
                    const detail = await api.academyInstaller(i.id);
                    setHistory(detail.history ?? []);
                    setForm({ ...form, completedAt: "", score: "" });
                    await load();
                  } finally {
                    setBusy(false);
                  }
                }}
                className="grid gap-3 sm:grid-cols-4"
              >
                <label className="block sm:col-span-2">
                  <span className="field-label">{tx.course}</span>
                  <select
                    className="field"
                    value={form.courseId}
                    onChange={(e) => setForm({ ...form, courseId: e.target.value })}
                  >
                    {courses.map((c) => (
                      <option key={c.id} value={c.id} className="bg-night">
                        {c.title} ({c.level})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="field-label">{tx.date}</span>
                  <input
                    type="date"
                    className="field num"
                    value={form.completedAt}
                    onChange={(e) => setForm({ ...form, completedAt: e.target.value })}
                    required
                  />
                </label>
                <label className="block">
                  <span className="field-label">{tx.score}</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="field num"
                    value={form.score}
                    onChange={(e) => setForm({ ...form, score: e.target.value })}
                  />
                </label>
                <div className="sm:col-span-4">
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-full bg-magenta px-4 py-2 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
                  >
                    {busy ? "…" : tx.save}
                  </button>
                </div>
              </form>

              {history.length > 0 && (
                <div className="mt-4">
                  <div className="brand-kicker">{tx.history}</div>
                  <ul className="mt-2 grid gap-1">
                    {history.map((h) => (
                      <li
                        key={h.id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[0.66rem] text-ice/70"
                      >
                        <span className="text-paper/90">
                          {h.course?.title ?? h.courseId}
                        </span>
                        <span className="num">{d(h.completedAt)}</span>
                        <span
                          className={cn(
                            "uppercase tracking-[0.1em]",
                            h.status === "passed" ? "text-green" : "text-pink",
                          )}
                        >
                          {h.status}
                        </span>
                        {h.expiresAt && <span className="num">→ {d(h.expiresAt)}</span>}
                        {h.score != null && <span className="num">{h.score}/100</span>}
                        <button
                          type="button"
                          onClick={async () => {
                            await api.deleteEnrollment(h.id);
                            const detail = await api.academyInstaller(i.id);
                            setHistory(detail.history ?? []);
                            await load();
                          }}
                          className="ml-auto text-[0.58rem] uppercase tracking-[0.1em] text-pink"
                        >
                          {tx.delete}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function Courses({
  tx,
  locale,
}: {
  tx: Record<keyof (typeof TEXT)["it"], string>;
  locale: "it" | "en" | "es";
}) {
  const [rows, setRows] = useState<AcademyCourse[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await api.academyCourses());
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setAdding((v) => !v)}
        className="rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ice/70"
      >
        + {tx.newCourse}
      </button>

      {adding && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const f = e.currentTarget;
            const data = new FormData(f);
            setBusy(true);
            try {
              await api.createAcademyCourse({
                code: String(data.get("code") ?? ""),
                title: String(data.get("title") ?? ""),
                level: String(data.get("level") ?? "silver"),
                validMonths: Number(data.get("validMonths") ?? 36),
                sortOrder: Number(data.get("sortOrder") ?? 0),
              });
              f.reset();
              setAdding(false);
              await load();
            } finally {
              setBusy(false);
            }
          }}
          className="glass-panel-strong mt-3 grid gap-3 p-5 sm:grid-cols-5"
        >
          <label className="block">
            <span className="field-label">{tx.code}</span>
            <input name="code" className="field" required placeholder="esterni_uv" />
          </label>
          <label className="block sm:col-span-2">
            <span className="field-label">Titolo</span>
            <input name="title" className="field" required />
          </label>
          <label className="block">
            <span className="field-label">{tx.required}</span>
            <select name="level" className="field" defaultValue="silver">
              {LEVELS.map((l) => (
                <option key={l} value={l} className="bg-night">
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="field-label">{tx.months}</span>
            <input
              name="validMonths"
              type="number"
              min={1}
              max={120}
              defaultValue={36}
              className="field num"
            />
          </label>
          <div className="sm:col-span-5">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
            >
              {busy ? "…" : tx.save}
            </button>
          </div>
        </form>
      )}

      <div className="mt-3 grid gap-2">
        {rows === null && <p className="text-sm text-ice/60">…</p>}
        {rows?.map((c) => (
          <article
            key={c.id}
            className={cn(
              "glass-panel-strong flex flex-wrap items-center gap-3 p-3.5",
              !c.active && "opacity-50",
            )}
          >
            <span className="min-w-0 flex-1 font-serif text-[0.92rem] text-paper">
              {c.title}
            </span>
            <span className="num text-[0.62rem] text-ice/45">{c.code}</span>
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-[0.55rem] font-semibold uppercase tracking-[0.14em]",
                LEVEL_STYLE[c.level],
              )}
            >
              {c.level}
            </span>
            <span className="num text-[0.64rem] text-ice/60">
              {c.validMonths} {locale === "it" ? "mesi" : locale === "es" ? "meses" : "months"}
            </span>
            <button
              type="button"
              onClick={async () => {
                await api.updateAcademyCourse(c.id, { active: !c.active });
                await load();
              }}
              className="rounded-full border border-white/15 px-3 py-1 text-[0.56rem] font-semibold uppercase tracking-[0.12em] text-ice/70"
            >
              {c.active ? tx.close : tx.save}
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}

function MyCredentials({
  tx,
  locale,
}: {
  tx: Record<keyof (typeof TEXT)["it"], string>;
  locale: "it" | "en" | "es";
}) {
  const [me, setMe] = useState<AcademyInstaller | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .academyMe()
      .then(setMe)
      .catch(() => setError(tx.noInstaller));
  }, [tx.noInstaller]);

  return (
    <AppShell>
      <div className="brand-kicker">{tx.kicker}</div>
      <h1 className="font-serif text-2xl text-paper sm:text-3xl">
        {tx.myCredentials}
      </h1>

      {error && <p className="mt-4 text-sm text-pink">{error}</p>}
      {!me && !error && <p className="mt-4 text-sm text-ice/60">…</p>}

      {me && (
        <div className="glass-panel-strong mt-5 p-5">
          <div className="font-serif text-lg text-paper">{me.companyName}</div>
          <div className="mt-2">
            <LevelPill
              level={me.academyLevel}
              validUntil={me.levelValidUntil}
              expiringSoon={me.expiringSoon}
              tx={tx}
            />
          </div>
          {me.patentinoNumber && (
            <div className="mt-2 flex items-center gap-1.5 text-[0.7rem] text-ice/75">
              <ShieldCheck className="h-4 w-4 text-goldbright" />
              <span className="num">{me.patentinoNumber}</span>
              <span className="text-ice/45">
                {d(me.patentinoIssuedAt)} → {d(me.patentinoExpiresAt)}
              </span>
            </div>
          )}

          <div className="mt-4 brand-kicker">{tx.credentials}</div>
          <CredentialGrid credentials={me.credentials} tx={tx} />
        </div>
      )}
    </AppShell>
  );
}
